import { useEffect, useState, useRef } from "react";
import axios from "axios";
import useEmployeeDetails from "../hooks/useEmployeeDetails";
import moment from "moment";
import WebcamCapture from "./WebcamCapture";
import CountdownTimer from "./CountdownTimer";
import { useNavigate } from "react-router-dom";
import sound from "../assests/beep-warning-6387.mp3"

const ShiftDetails = () => {
  const { id, employeeToken } = useEmployeeDetails();
  const [checkinStatus, setCheckinStatus] = useState(null);
  const [checkinTime, setCheckinTime] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showWebcam, setShowWebcam] = useState(false);
  const [webcamAction, setWebcamAction] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BASE_URL;
  const [alertShown, setAlertShown] = useState(false);


  // 🔊 extra audio for logout reminder
  const logoutAudioRef = useRef(null);

  useEffect(() => {
    logoutAudioRef.current = new Audio(sound); // upload custom strong sound
    logoutAudioRef.current.load();
  }, []);

  const playLogoutSound = () => {
    if (logoutAudioRef.current) {
      logoutAudioRef.current.play().catch((err) => console.error("Logout sound failed:", err));
    }
  };

  // 🔹 Ask for Notification Permission once
  useEffect(() => {
    if ("Notification" in window) {
      if (Notification.permission !== "granted") {
        Notification.requestPermission().then((permission) => {
          console.log("Notification permission:", permission);
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!id || !employeeToken || hasFetched.current) {
      if (!employeeToken) navigate("/servererror");
      return;
    }

    hasFetched.current = true;

    axios
      .post(
        `${API_URL}/check_shift_checkin`,
        { user_id: id },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${employeeToken}`,
          },
        }
      )
      .then((res) => {
        const data = res.data;
        setCheckinStatus(data?.checkin_status ?? false);
        setCheckinTime(data?.checkin_time ?? "");
        setEmployeeName(data?.name ?? "Employee");
        setErrorMessage("");

        if (data?.checkin_status && data?.checkin_id && data?.checkin_time) {
          localStorage.setItem("checkin_id", data.checkin_id);
          localStorage.setItem("checkin_time", data.checkin_time);
        } else {
          localStorage.removeItem("checkin_id");
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        setErrorMessage("Server error. Please try again later.");
        setCheckinStatus(false);
        setCheckinTime("");
        navigate("/servererror");
      });
  }, [id, employeeToken, navigate, API_URL]);

  const handleCheckInSuccess = (responseData) => {
    setCheckinStatus(true);
    setCheckinTime(responseData?.checkin_time ?? "");
    setEmployeeName(responseData?.name ?? "Employee");

    if (responseData?._id) {
      localStorage.setItem("checkin_id", responseData._id);
    }
  };

  const handleCheckOutSuccess = () => {
    setCheckinStatus(false);
    setCheckinTime("");
    localStorage.removeItem("_id");
  };

  const handleCheckIn = () => {
    setWebcamAction("checkin");
    setShowWebcam(true);
  };

  const handleCheckOut = () => {
    setWebcamAction("checkout");
    setShowWebcam(true);
  };

  const shouldPromptCheckIn = !checkinStatus && !checkinTime;

  // 🔹 Show notification + sound before 9 hours
  // useEffect(() => {
  //   if (!checkinTime) return;

  //   const interval = setInterval(() => {
  //     const now = moment();
  //     const loginMoment = moment(checkinTime);
  //     const diffMinutes = now.diff(loginMoment, "minutes");

  //     // 9 hrs = 540 mins → Warn at 535 mins
  //     if (diffMinutes >= 210 && diffMinutes < 540 && !alertShown) {
  //       setAlertShown(true);

  //       // ✅ Notification
  //       if ("Notification" in window && Notification.permission === "granted") {
  //         new Notification("⚠️ Shift Alert", {
  //           body: "You have 5 minutes left before completing 9 hours of login.",
  //           icon: "/icon.png", // optional
  //         });
  //       } else {
  //         alert("⚠️ You have 5 minutes left before completing 9 hours of login.");
  //       }

  //       // ✅ Play sound
  //       playSound();
  //     }
  //   }, 60 * 1000);

  //   return () => clearInterval(interval);
  // }, [checkinTime, alertShown]);

  useEffect(() => {
    if (!checkinTime) return;

    const interval = setInterval(() => {
      const now = moment();
      const loginMoment = moment(checkinTime);
      const diffMinutes = now.diff(loginMoment, "minutes");

      // 5 minutes before 9 hrs
      if (diffMinutes >= 535 && diffMinutes < 540 && !alertShown) {
        setAlertShown(true);

        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("⚠️ Shift Alert", {
            body: "You have 5 minutes left before completing 9 hours of login.",
            icon: "/icon.png",
          });
        } else {
          alert("⚠️ You have 5 minutes left before completing 9 hours of login.");
        }

        playLogoutSound();
      }

      // ⏰ Exactly at 9 hrs
      if (diffMinutes === 540) {
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("⏰ Shift Completed!", {
            body: "You’ve completed 9 hours. Please log out now.",
            icon: "/icon.png",
          });
        } else {
          alert("⏰ You’ve completed 9 hours. Please log out now.");
        }

        playLogoutSound(); // play strong reminder music
      }
    }, 60 * 1000); // check every minute

    return () => clearInterval(interval);
  }, [checkinTime, alertShown]);


  return (
    <div className="relative flex flex-col shadow-md rounded-lg bg-white p-4 gap-3 sm:max-w-md mx-auto border border-gray-300">
      <div className="flex justify-between items-start">
        <div className="text-xs text-green-600">
          {checkinStatus ? "✅" : "⏺"}
        </div>
        <div className="text-sm text-green-800 flex">
          Duration {checkinTime ? <CountdownTimer createdAt={checkinTime} /> : "--"}
        </div>
      </div>

      <div className="text-center text-gray-700 font-medium text-sm">
        <b> CheckIn-At : </b>{" "}
        {checkinTime ? moment.utc(checkinTime).format("DD-MM-YY hh:mm A") : "--"}
      </div>

      {shouldPromptCheckIn && (
        <div className="text-center text-green-700 font-semibold">
          Hello {employeeName}, please check in to start your shift!
        </div>
      )}

      {errorMessage && (
        <div className="text-center text-red-500 font-medium">
          {errorMessage}
        </div>
      )}

      <div className="flex justify-between mt-4">
        <button
          className="flex-1 mr-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          disabled={checkinStatus}
          onClick={handleCheckIn}
        >
          Check-In
        </button>
        <button
          className="flex-1 ml-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          disabled={!checkinStatus}
          onClick={handleCheckOut}
        >
          Check-Out
        </button>
      </div>

      {showWebcam && (
        <WebcamCapture
          mode={webcamAction}
          onClose={() => setShowWebcam(false)}
          onCheckInSuccess={handleCheckInSuccess}
          onCheckOutSuccess={handleCheckOutSuccess}
        />
      )}
    </div>
  );
};

export default ShiftDetails;
